import React, { useState, useEffect, useRef } from 'react';

const BouncingNod = () => {
  const [images, setImages] = useState([]);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 640);
  const containerRef = useRef(null);
  const animationRef = useRef(null);
  const audioRef = useRef(null);
  const timeRef = useRef(0);

  const preloadImage = (src) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        const scale = window.innerWidth <= 640 ? 0.5 : 1;
        resolve({
          src,
          naturalWidth: img.naturalWidth,
          naturalHeight: img.naturalHeight,
          displayWidth: img.naturalWidth * scale,
          displayHeight: img.naturalHeight * scale
        });
      };
      img.onerror = reject;
      img.src = src;
    });
  };

  const createImageObject = (imageData) => ({
    ...imageData,
    x: Math.random() * (window.innerWidth - imageData.displayWidth),
    y: Math.random() * (window.innerHeight - imageData.displayHeight),
    vx: (Math.random() - 0.5) * (window.innerWidth <= 640 ? 0.5 : 1),
    vy: (Math.random() - 0.5) * (window.innerWidth <= 640 ? 0.5 : 1),
    phaseOffset: Math.random() * Math.PI * 2 
  });

  const handleImageClick = () => {
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(error => {
        console.error('Error playing audio:', error);
      });
    }
  };

  useEffect(() => {
    const imageNames = [
      'nod1.png', 'nod2.png', 'nod3.png', 'nod4.png', 'nod5.png',
      'nod6.png', 'nod7.png', 'nod8.png', 'nod9.png', 'nod10.png',
      'nod11.png', 'nod12.png', 'nod13.png', 'nod14.png',
    ];
  
    const loadImages = async () => {
      try {
        const randomImages = [...imageNames]
          .sort(() => Math.random() - 0.5)
          .slice(0, 10);
        
        const imagePromises = randomImages.map(name => 
          preloadImage(`/img/nod/${name}`)
        );
        
        const loadedImages = await Promise.all(imagePromises);
        const imageObjects = loadedImages.map(createImageObject);
        setImages(imageObjects);
      } catch (error) {
        console.error('Error loading images:', error);
      }
    };
  
    loadImages();
  
    const handleResize = () => {
      const newIsMobile = window.innerWidth <= 640;
      if (newIsMobile !== isMobile) {
        setIsMobile(newIsMobile);
        loadImages();
      }
    };
  
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isMobile]);

  useEffect(() => {
    if (!images.length) return;

    const animate = () => {
      timeRef.current += 0.005;

      setImages(prevImages => prevImages.map(img => {
        let newX = img.x + img.vx;
        let newY = img.y + img.vy;
        let newVx = img.vx;
        let newVy = img.vy;

        if (newX <= 0 || newX + img.displayWidth >= window.innerWidth) {
          newVx = -newVx;
          newX = newX <= 0 ? 0 : window.innerWidth - img.displayWidth;
        }
        if (newY <= 0 || newY + img.displayHeight >= window.innerHeight) {
          newVy = -newVy;
          newY = newY <= 0 ? 0 : window.innerHeight - img.displayHeight;
        }

        return {
          ...img,
          x: newX,
          y: newY,
          vx: newVx,
          vy: newVy
        };
      }));

      animationRef.current = requestAnimationFrame(animate);
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [images.length]);

  return (
    <div 
      ref={containerRef}
      className="absolute inset-0 overflow-hidden"
    >
      <audio ref={audioRef} src="/bgm/fc.mp3" preload="auto" />
      {images.map((img, index) => (
        <img
          key={index}
          src={img.src}
          alt={`Bouncing nod ${index + 1}`}
          className="absolute filter grayscale opacity-50 cursor-pointer hover:filter-none hover:opacity-75 transition-all duration-300"
          style={{
            transform: `translate(${img.x}px, ${img.y}px) scale(${1 + Math.sin(timeRef.current + img.phaseOffset) * 0.1})`,
            width: img.displayWidth,
            height: img.displayHeight,
            transition: 'transform 0.3s linear'
          }}
          onClick={handleImageClick}
          onTouchStart={handleImageClick}
        />
      ))}
    </div>
  );
};

export default BouncingNod;